import { v4 as uuidv4 } from 'uuid';
import offlineStorage from './offlineStorage';
import api from '../utils/api';

class SyncService {
  constructor() {
    this.offlineStorage = offlineStorage;
    this.syncInterval = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = [];
    
    // 监听网络状态变化
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.isOnline = true;
    this.notifyListeners('online');
    // 网络恢复时自动同步
    this.syncPendingTransactions();
  };

  handleOffline = () => {
    this.isOnline = false;
    this.notifyListeners('offline');
  };

  addListener(listener) {
    this.listeners.push(listener);
  }

  removeListener(listener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  notifyListeners(event, data) {
    this.listeners.forEach(listener => listener(event, data));
  }

  // 开始自动同步
  startAutoSync(intervalMs = 30000) {
    this.stopAutoSync();
    this.syncInterval = setInterval(() => {
      if (this.isOnline && !this.syncInProgress) {
        this.syncPendingTransactions();
      }
    }, intervalMs);
  }

  // 停止自动同步
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // 同步待处理的交易
  async syncPendingTransactions() {
    if (this.syncInProgress || !this.isOnline) return;
    
    this.syncInProgress = true;
    this.notifyListeners('sync-start');
    
    try {
      const pendingTransactions = await offlineStorage.getUnsyncedTransactions();
      
      if (pendingTransactions.length === 0) {
        this.notifyListeners('sync-complete', { synced: 0 });
        return;
      }
      
      const inboundTransactions = pendingTransactions.filter(t => t.type === 'inbound');
      const outboundTransactions = pendingTransactions.filter(t => t.type === 'outbound');

      const results = [];
      
      for (const transaction of inboundTransactions) {
        try {
          console.log('Syncing inbound transaction:', transaction);
          await api.post('/inventory/inbound', transaction);
          results.push({ uuid: transaction.uuid, status: 'success' });
        } catch (error) {
          console.error('Sync error for transaction:', transaction, 'Error:', error.response?.data || error.message);
          
          const errorStatus = error.response?.status;
          const errorMessage = error.response?.data?.error || error.message;
          
          // 不可恢复的错误，直接删除
          if ([400, 404, 422, 409].includes(errorStatus) || 
              errorMessage.includes('已出库') || 
              errorMessage.includes('不存在') ||
              errorMessage.includes('已售')) {
            results.push({ uuid: transaction.uuid, status: 'fatal_error', message: errorMessage });
            await offlineStorage.deleteTransaction(transaction.uuid);
          }
          // 服务器错误或网络错误，增加重试计数
          else {
            const retryCount = await offlineStorage.updateTransactionRetry(transaction.uuid, errorMessage);
            
            // 重试次数过多，删除
            if (retryCount >= 3) {
              results.push({ uuid: transaction.uuid, status: 'max_retries_exceeded', message: `重试${retryCount}次后仍失败: ${errorMessage}` });
              await offlineStorage.deleteTransaction(transaction.uuid);
            } else {
              results.push({ uuid: transaction.uuid, status: 'error', message: errorMessage, retryCount });
            }
          }
        }
      }
      
      // 同步出库交易（类似处理）
      for (const transaction of outboundTransactions) {
        try {
          await api.post('/inventory/outbound', transaction);
          results.push({ uuid: transaction.uuid, status: 'success' });
        } catch (error) {
          const errorStatus = error.response?.status;
          const errorMessage = error.response?.data?.error || error.message;
          
          if ([400, 404, 422, 409].includes(errorStatus) || 
              errorMessage.includes('已出库') || 
              errorMessage.includes('不存在')) {
            await offlineStorage.deleteTransaction(transaction.uuid);
          } else {
            const retryCount = await offlineStorage.updateTransactionRetry(transaction.uuid, errorMessage);
            if (retryCount >= 3) {
              await offlineStorage.deleteTransaction(transaction.uuid);
            }
          }
        }
      }
      

      let syncedCount = 0;
      
      for (const result of results) {
        if (result.status === 'success') {
          await offlineStorage.markTransactionSynced(result.uuid);
          syncedCount++;
        } else if (result.status === 'duplicate') {
          // 如果是重复的，直接删除本地记录
          await offlineStorage.deleteTransaction(result.uuid);
        }
      }
      
      this.notifyListeners('sync-complete', { synced: syncedCount });
      
      // 清理旧的已同步交易
      await offlineStorage.cleanupSyncedTransactions();

      // 清理失败的交易
      const cleanedCount = await offlineStorage.cleanupFailedTransactions();
      if (cleanedCount > 0) {
        console.log(`清理了 ${cleanedCount} 个失败的事务`);
      }
      
    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners('sync-error', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // 保存交易（在线或离线）
  async saveTransaction(transaction) {
    const transactionWithUuid = {
      ...transaction,
      uuid: transaction.uuid || uuidv4(),
      created_at: new Date().toISOString() // 添加创建时间
    };
    
    if (this.isOnline) {
      try {
        // 根据新的数据结构选择不同的端点
        let endpoint;
        if (transaction.type === 'inbound') {
          endpoint = '/inventory/inbound';
        } else if (transaction.type === 'outbound') {
          endpoint = '/inventory/outbound';
        } else {
          throw new Error('Invalid transaction type');
        }
        
        const response = await api.post(endpoint, transactionWithUuid);
        
        if (response.data.success) {
          this.notifyListeners('transaction-saved', {
            ...transactionWithUuid,
            synced: true
          });
          return response.data;
        }
      } catch (error) {
        console.error('Failed to save online, saving offline:', error);
        // 网络错误时自动切换到离线模式
        this.isOnline = false;
        this.notifyListeners('network-error', error);
      }
    }
    
    // 离线保存
    try {
      const savedTransaction = await offlineStorage.saveTransaction(transactionWithUuid);
      this.notifyListeners('transaction-saved', savedTransaction);
      this.notifyListeners('offline-save', { count: await this.getOfflineCount() });
      return savedTransaction;
    } catch (error) {
      console.error('Failed to save offline:', error);
      throw new Error('保存失败：无法在线保存，且离线存储不可用');
    }
  }

  // 获取离线交易数量
  async getOfflineCount() {
    try {
      const transactions = await offlineStorage.getUnsyncedTransactions();
      return transactions.length;
    } catch (error) {
      return 0;
    }
  }

  // 获取缓存数据
  async getCachedData() {
    const [products, warehouses, batches] = await Promise.all([
      offlineStorage.getCachedProducts(),
      offlineStorage.getCachedWarehouses(),
      offlineStorage.getCachedBatches()
    ]);
    
    return { products, warehouses, batches };
  }

  // 更新缓存数据
  async updateCachedData() {
    if (!this.isOnline) return;
    
    try {
      const [productsRes, warehousesRes, batchesRes] = await Promise.all([
        api.get('/products'),
        api.get('/warehouses'),
        api.get('/inventory/batches')
      ]);
      
      await Promise.all([
        offlineStorage.cacheProducts(productsRes.data),
        offlineStorage.cacheWarehouses(warehousesRes.data),
        offlineStorage.cacheBatches(batchesRes.data)
      ]);
      
      this.notifyListeners('cache-updated');
    } catch (error) {
      console.error('Failed to update cache:', error);
    }
  }
}

export default new SyncService();